import { Button } from '@base-ui/react/button';
import { Dialog } from '@base-ui/react/dialog';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useMediaUpload } from '@/admin/content/MediaPicker';
import buttonStyles from '@/admin/styles/button.module.css';
import { ConfirmDialog } from '@/admin/ui/ConfirmDialog';
import { ErrorText, FieldHint } from '@/admin/ui/Form';
import { PageTitle } from '@/admin/ui/Page';
import { Pagination } from '@/admin/ui/Pagination';
import api, { queryClient } from '@/api';
import { errorCode } from '@/api/errors';
import type { components } from '@/api/qraft/schema';

type MediaLibraryItem = components['schemas']['MediaLibraryItem'];
type MediaLibraryReference = components['schemas']['MediaLibraryReference'];

const PAGE_SIZE = 24;
const SEARCH_DEBOUNCE_MS = 300;

export const Route = createFileRoute('/admin/media/')({
  staticData: { minTier: 'ADMIN' },
  component: MediaLibrary,
});

function invalidateMedia(id?: number) {
  queryClient.invalidateQueries({
    queryKey: api.adminListMedia.getQueryKey({}),
  });
  if (id !== undefined)
    queryClient.invalidateQueries({
      queryKey: api.adminGetMedia.getQueryKey({ path: { media_id: id } }),
    });
}

function MediaLibrary() {
  const { t } = useTranslation();
  const input = useRef<HTMLInputElement>(null);
  const [page, setPage] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<number | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(0);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data, isLoading, error } = api.adminListMedia.useQuery({
    query: {
      limit: PAGE_SIZE,
      offset: PAGE_SIZE * page,
      ...(search ? { search } : {}),
    },
  });
  const items = data?.data ?? [];

  const {
    upload,
    uploading,
    error: uploadError,
    clearError,
  } = useMediaUpload(() => {
    setPage(0);
    invalidateMedia();
  });

  const pager = (hideWhenAlone = false) => (
    <Pagination
      page={page}
      pageSize={PAGE_SIZE}
      shown={items.length}
      total={data?.count}
      onPage={setPage}
      hideWhenAlone={hideWhenAlone}
    />
  );

  return (
    <div>
      <Header>
        <PageTitle>{t('admin.media.title')}</PageTitle>
        <input
          ref={input}
          type="file"
          accept="image/jpeg,image/png,image/gif,image/webp"
          aria-label={t('admin.media.imageFile')}
          hidden
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            if (file) upload(file);
          }}
        />
        <Button
          className={buttonStyles.Button}
          disabled={uploading}
          onClick={() => {
            clearError();
            input.current?.click();
          }}
        >
          {uploading
            ? t('admin.media.uploading')
            : t('admin.media.uploadImage')}
        </Button>
      </Header>
      <Toolbar>
        <SearchInput
          type="search"
          aria-label={t('admin.media.searchLabel')}
          placeholder={t('admin.media.searchPlaceholder')}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
        <FieldHint>{t('admin.media.formatHint')}</FieldHint>
      </Toolbar>
      {uploadError && <ErrorText role="alert">{uploadError}</ErrorText>}
      {pager()}

      {isLoading ? (
        <Note>{t('admin.media.loading')}</Note>
      ) : error ? (
        <Note $error>{t('admin.media.errorLoading')}</Note>
      ) : items.length === 0 ? (
        <Note>
          {search ? t('admin.media.noMatches') : t('admin.media.empty')}
        </Note>
      ) : (
        <Grid>
          {items.map((item) => (
            <Tile
              key={item.id}
              type="button"
              onClick={() => setSelected(item.id)}
            >
              <img src={item.url} alt={item.filename} loading="lazy" />
              <span>{item.filename}</span>
            </Tile>
          ))}
        </Grid>
      )}
      {pager(true)}

      <MediaDrawer id={selected} onClose={() => setSelected(null)} />
    </div>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Where an item is used, and the only place it can be deleted from.
function MediaDrawer({
  id,
  onClose,
}: {
  id: number | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [confirming, setConfirming] = useState(false);

  const detail = api.adminGetMedia.useQuery(
    { path: { media_id: id ?? 0 } },
    { enabled: id !== null },
  );
  const item: MediaLibraryItem | undefined = detail.data;
  const references = detail.data?.references ?? [];

  const remove = api.adminDeleteMedia.useMutation(undefined, {
    meta: { toast: t('admin.common.deleted') },
    onSuccess: () => {
      invalidateMedia();
      setConfirming(false);
      onClose();
    },
    onError: () => {
      // The reference list may have changed under us; show the current one.
      if (id !== null) invalidateMedia(id);
    },
  });
  const inUse = errorCode(remove.error) === 'media_in_use';

  return (
    <Dialog.Root
      open={id !== null}
      onOpenChange={(next) => {
        if (!next) {
          remove.reset();
          onClose();
        }
      }}
    >
      <Dialog.Portal>
        <Backdrop />
        <Drawer>
          <Dialog.Title render={<DrawerTitle />}>
            {item?.filename ?? t('admin.media.details')}
          </Dialog.Title>
          {detail.isLoading ? (
            <Note>{t('admin.media.loading')}</Note>
          ) : detail.error || !item ? (
            <Note $error>{t('admin.media.errorLoadingOne')}</Note>
          ) : (
            <>
              <Preview src={item.url} alt={item.filename} />
              <Facts>
                <dt>{t('admin.media.size')}</dt>
                <dd>
                  {item.width} × {item.height} · {formatBytes(item.bytes)}
                </dd>
                <dt>{t('admin.media.uploadedBy')}</dt>
                <dd>
                  {item.uploaded_by?.name ?? '—'} ·{' '}
                  {new Date(item.created_at).toLocaleString()}
                </dd>
                <dt>{t('admin.media.key')}</dt>
                <dd>
                  <code>{item.key}</code>
                </dd>
              </Facts>
              <h3>{t('admin.media.references')}</h3>
              {references.length === 0 ? (
                <FieldHint>{t('admin.media.noReferences')}</FieldHint>
              ) : (
                <References>
                  {references.map((r) => (
                    <li
                      key={`${r.entity}-${r.id}-${r.version ?? 0}-${r.field}`}
                    >
                      <ReferenceLabel reference={r} />
                    </li>
                  ))}
                </References>
              )}
              {inUse && (
                <ErrorText role="alert">{t('admin.media.inUse')}</ErrorText>
              )}
              {remove.isError && !inUse && (
                <ErrorText role="alert">
                  {t('admin.media.deleteError')}
                </ErrorText>
              )}
              <DrawerActions>
                <Dialog.Close className={buttonStyles.Button}>
                  {t('admin.common.close')}
                </Dialog.Close>
                <Button
                  className={buttonStyles.Button}
                  disabled={remove.isPending}
                  onClick={() => {
                    remove.reset();
                    setConfirming(true);
                  }}
                >
                  {t('admin.common.delete')}
                </Button>
              </DrawerActions>
              <ConfirmDialog
                open={confirming}
                title={t('admin.media.deleteTitle')}
                confirmLabel={t('admin.common.delete')}
                tone="danger"
                pending={remove.isPending}
                onConfirm={() => {
                  setConfirming(false);
                  remove.mutate({ path: { media_id: item.id } });
                }}
                onCancel={() => setConfirming(false)}
              >
                <p>{t('admin.media.deleteBody')}</p>
              </ConfirmDialog>
            </>
          )}
        </Drawer>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function ReferenceLabel({ reference }: { reference: MediaLibraryReference }) {
  const { t } = useTranslation();
  return (
    <>
      {t(`admin.media.entity.${reference.entity}`)}: {reference.title}{' '}
      <Meta>
        #{reference.id}
        {reference.version != null ? ` v${reference.version}` : ''} ·{' '}
        {reference.field}
      </Meta>
    </>
  );
}

const Header = styled.div`
  align-items: center;
  display: flex;
  gap: var(--component-margin);
  justify-content: space-between;
`;

const Toolbar = styled.div`
  align-items: center;
  display: flex;
  gap: var(--component-margin);
  margin: var(--component-margin);
`;

const SearchInput = styled.input`
  border: 1px solid var(--grey-300);
  border-radius: 4px;
  font-size: 0.9rem;
  padding: 0.4rem 0.6rem;
`;

const Grid = styled.div`
  display: grid;
  gap: 0.75rem;
  grid-template-columns: repeat(auto-fill, minmax(170px, 1fr));
  margin: var(--component-margin);
`;

const Tile = styled.button`
  background: var(--grey-75);
  border: 1px solid var(--grey-200);
  border-radius: 0.5rem;
  color: var(--grey-800);
  cursor: pointer;
  display: flex;
  flex-direction: column;
  font-family: inherit;
  font-size: 0.8125rem;
  gap: 0.25rem;
  padding: 0.375rem;
  text-align: left;

  &:hover,
  &:focus-visible {
    border-color: var(--blue);
  }

  img {
    background: var(--grey-100);
    border-radius: 4px;
    height: 120px;
    object-fit: cover;
    width: 100%;
  }

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const Note = styled.div<{ $error?: boolean }>`
  color: ${(p) => (p.$error ? 'var(--error)' : 'var(--grey-600)')};
  padding: 2rem;
  text-align: center;
`;

const Backdrop = styled(Dialog.Backdrop)`
  background-color: rgba(0, 0, 0, 0.4);
  inset: 0;
  position: fixed;
  z-index: 100;
`;

const Drawer = styled(Dialog.Popup)`
  background-color: var(--component-color);
  bottom: 0;
  box-shadow: var(--shadow-popover);
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  max-width: 100vw;
  overflow-y: auto;
  padding: 1.5rem;
  position: fixed;
  right: 0;
  top: 0;
  width: 420px;
  z-index: 101;
`;

const DrawerTitle = styled.h2`
  color: var(--grey-900);
  font-size: 1.125rem;
  font-weight: 600;
  margin: 0;
  overflow-wrap: anywhere;
`;

const Preview = styled.img`
  background: var(--grey-100);
  border-radius: 6px;
  max-height: 260px;
  object-fit: contain;
  width: 100%;
`;

const Facts = styled.dl`
  display: grid;
  gap: 0.25rem 0.75rem;
  grid-template-columns: auto 1fr;
  margin: 0;

  dt {
    color: var(--grey-600);
    font-size: 0.8125rem;
  }

  dd {
    margin: 0;
    overflow-wrap: anywhere;
  }
`;

const References = styled.ul`
  margin: 0;
  padding-left: 1.25rem;
`;

const Meta = styled.span`
  color: var(--grey-600);
  font-size: 0.8125rem;
`;

const DrawerActions = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
`;
